import React, { useCallback, useEffect, useMemo } from "react";
import { EMAIL_ENTITY_TYPE } from "@miplatform/constants";
import {
  CalendarType,
  IAttemptListItemResponseForWorkOrder,
  IAttemptListResponse,
} from "@miplatform/types";
import { EmailActivityPreviewModal } from "@mibuildweb/components";
import { Box } from "@mui/material";
import { useCalendarContext } from "../../providers";
import { OrdersEmailDrawer } from "../Orders/OrdersEmailDrawer/OrdersEmailDrawer";
import { useOrdersContext } from "../Orders/OrdersProvider";
import { AutoHoldList } from "./AutoHoldList/AutoHoldList";
import { CreateWorkOrderDrawer } from "./CreateWorkOrderDrawer/CreateWorkOrderDrawer";
import { HoldsListCalendar } from "./HoldsListCalendar";
import { KanbanCalendar } from "./NHCKanbanCalendar/KanbanCalendar";
import { NHCPriorityScheduler } from "./NHCPriorityScheduler/NHCPriorityScheduler";
import { SingleDayScheduler } from "./SingleDayScheduler";
import { WorkOrderAttemptDrawer } from "./WorkOrderAttemptDrawer/WorkOrderAttemptDrawer";
import { useNhcBoardViewModel } from "./useNhcBoardViewModel";

export const CalendarRenderer = ({
  setSelectedOrder,
}: CalendarRendererProps) => {
  const {
    emailDrawer,
    emailPreview,
    handleEmailDrawerClose,
    handleEmailPreviewModalClose,
  } = useOrdersContext();

  const {
    date,
    selectedAttempt,
    isCreateWorkOrderDrawerOpen,
    calendarConfig,
    calendarSet,
    isAssigneeMode,
    isCalendarLoading,
    startWorkOrder,
    updateAttempt,
    holdWorkOrder,
    resetWorkOrder,
    highlightAttempts,
    submitWorkOrder,
    closeWorkOrder,
    getCalendarCounts,
    setSelectedAttempt,
    refreshData,
    getTrayDataForCalendar,
    handleCreateWorkOrderDrawerClose,
    activeCalendarId,
  } = useCalendarContext();

  const { orderReasons, getOrderReasons } = useNhcBoardViewModel();

  useEffect(() => {
    getOrderReasons();
  }, [calendarSet?.config?.id]);

  const customWorkTemplateBase = useMemo(
    () =>
      calendarConfig?.workTemplateBases?.find((template) => template.isCustom),
    [calendarConfig?.workTemplateBases]
  );

  const handleHoldsRefresh = useCallback(() => {
    refreshData();
    getTrayDataForCalendar();
  }, [refreshData, getTrayDataForCalendar]);

  const handleWorkOrderAttemptDrawerClose = useCallback(() => {
    setSelectedAttempt(null);
  }, [setSelectedAttempt]);

  const calendarView = useMemo(() => {
    if (!calendarConfig) {
      return null;
    }

    switch (calendarConfig.calendarType) {
      case CalendarType.Scheduler:
        return (
          <SingleDayScheduler
            calendar={calendarConfig}
            date={date}
            calendarSet={calendarSet}
          />
        );
      case CalendarType.PriorityScheduler:
        return <NHCPriorityScheduler />;
      case CalendarType.Kanban:
        return (
          <KanbanCalendar
            orderReasons={orderReasons}
            setSelectedOrder={setSelectedOrder}
          />
        );
      case CalendarType.HoldsList:
        return (
          <HoldsListCalendar
            key={activeCalendarId}
            onRefresh={handleHoldsRefresh}
            orderReasons={orderReasons}
          />
        );
      case CalendarType.AutoHold:
        return <AutoHoldList />;
      default:
        return <Box>Unknown calendar type: {calendarConfig.calendarType}</Box>;
    }
  }, [
    calendarConfig?.id,
    calendarSet?.config?.id,
    date,
    handleHoldsRefresh,
    orderReasons,
    setSelectedOrder,
    activeCalendarId,
  ]);

  return (
    <>
      {calendarView}
      {emailDrawer && (
        <OrdersEmailDrawer
          // @ts-expect-error fixing this error will require to change the OrdersEmailDrawer to typescript
          open={emailDrawer.isOpen}
          entity={emailDrawer.order}
          entityType={EMAIL_ENTITY_TYPE.ORDER}
          templateType={emailDrawer.templateType}
          onClose={handleEmailDrawerClose}
        />
      )}
      {selectedAttempt && (
        <WorkOrderAttemptDrawer
          key={selectedAttempt.id}
          open={!!selectedAttempt}
          listAttempt={selectedAttempt}
          isAssigneeMode={isAssigneeMode}
          isCalendarLoading={isCalendarLoading}
          calendarSet={calendarSet}
          startWorkOrder={startWorkOrder}
          updateAttempt={updateAttempt}
          holdWorkOrder={holdWorkOrder}
          resetWorkOrder={resetWorkOrder}
          highlightAttempts={highlightAttempts}
          submitWorkOrder={submitWorkOrder}
          closeWorkOrder={closeWorkOrder}
          getCalendarCounts={getCalendarCounts}
          onClose={handleWorkOrderAttemptDrawerClose}
        />
      )}
      {emailPreview && (
        <EmailActivityPreviewModal
          isEmailModalOpen
          activity={emailPreview}
          details={emailPreview}
          closeEmailModal={handleEmailPreviewModalClose}
        />
      )}
      <CreateWorkOrderDrawer
        open={isCreateWorkOrderDrawerOpen}
        customWorkTemplateBase={customWorkTemplateBase}
        onClose={handleCreateWorkOrderDrawerClose}
        onRefresh={getTrayDataForCalendar}
      />
    </>
  );
};

interface CalendarRendererProps {
  setSelectedOrder: (
    order:
      | IAttemptListResponse
      | IAttemptListItemResponseForWorkOrder
      | undefined
  ) => void;
}
